'use client'

import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'

/**
 * Client boundary for the map — and the gate that keeps MapLibre off the critical path.
 *
 * MapLibre needs a browser, so CartoMap must load with `ssr: false`. In the Next 14 App Router,
 * `dynamic(..., { ssr: false })` is only legal inside a CLIENT component: calling it from a server
 * component throws at hydration. This wrapper is that boundary, so CityCarto stays a server
 * component and keeps the h1, the prose and the full park list in the server-rendered HTML.
 *
 * WHY IT IS ALSO GATED (2026-07-13). `dynamic()` code-splits the chunk but still fetches and
 * EVALUATES it the moment this component mounts. Measured on the Madrid city page, mobile:
 *
 *     performance 33/100 · LCP 9.1s · TBT 2,950ms · script evaluation 6,309ms
 *     the MapLibre chunk alone: 272KB
 *
 * The map is the signature of this design and it is not going anywhere — but it is not the CONTENT.
 * The content (h1, 153 park rows, the prose) is server-rendered and readable with no JS at all. So
 * the map now waits for the main thread to be idle AND for its own frame to come near the viewport
 * before it loads. A reader who scrolls to it still gets it seamlessly; a reader who lands, reads
 * the list and taps a park never pays for it.
 *
 * `requestIdleCallback` is the important half: on mobile the map frame is often already near the
 * viewport at load, so an IntersectionObserver alone would fire immediately and buy nothing.
 */
const CartoMap = dynamic(() => import('./CartoMap'), {
  ssr: false,
  loading: () => (
    <div className="mapframe">
      <div className="mapcanvas" />
    </div>
  ),
})

const idle = (cb) =>
  typeof window !== 'undefined' && 'requestIdleCallback' in window
    ? window.requestIdleCallback(cb, { timeout: 2500 })
    : setTimeout(cb, 1200)

export default function MapMount(props) {
  const holder = useRef(null)
  const [load, setLoad] = useState(false)

  useEffect(() => {
    if (load) return
    let done = false
    const go = () => {
      if (done) return
      done = true
      setLoad(true)
    }

    // No IntersectionObserver (old browser): just wait for idle and load it anyway.
    if (typeof IntersectionObserver === 'undefined') {
      idle(go)
      return
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect()
          // In view — but still wait for the main thread to be free. Loading a 272KB WebGL library
          // while the browser is mid-hydration is exactly what produced a 2,950ms blocking time.
          idle(go)
        }
      },
      // Start a screen early, so it is ready by the time the reader arrives.
      { rootMargin: '400px 0px' },
    )

    if (holder.current) io.observe(holder.current)
    return () => io.disconnect()
  }, [load])

  if (!load) {
    // Same box, same height — nothing moves when the real map arrives. CLS stays 0.
    return (
      <div ref={holder} className="mapframe" aria-hidden="true">
        <div className="mapcanvas" />
      </div>
    )
  }

  return <CartoMap {...props} />
}
